/**
 * DashboardLayout.js
 *
 * LOGIN LOGS NAV FIX (this version)
 *   The 'Login Logs' ALL_NAV entry added in the previous version had
 *   `icon: LogIn` (a raw component reference) instead of `icon: 'LogIn'`
 *   (a string key, the pattern every other nav item uses). This caused
 *   two bugs:
 *     1. `LogIn` was not imported from lucide-react, so the reference
 *        was undefined at runtime.
 *     2. Even if it had been imported, the render code does
 *        `const Icon = IconMap[item.icon]`, which expects a string key —
 *        passing a component reference directly would have resolved to
 *        `IconMap[<Function>]` = undefined, so no icon would render.
 *   Fixed: `LogIn` added to the lucide-react import, `LogIn` added to
 *   IconMap, and the nav entry changed to `icon: 'LogIn'`.
 *   Everything else is byte-for-byte identical to the version provided.
 *
 * FEATURE (previous version) — plan-based navigation filtering mechanism.
 *   Each ALL_NAV item can now optionally include a `plans: [...]` array,
 *   mirroring exactly how `roles: [...]` already works. If an item has
 *   no `plans` array (the case for EVERY existing item below — none
 *   have been touched), it remains visible to every plan, exactly as
 *   today. This is purely additive: nothing changes visually right now,
 *   but the mechanism is ready whenever specific nav items should be
 *   restricted to certain plan tiers (e.g. `plans: ['growth','enterprise']`
 *   to hide an item from a 'starter' plan).
 *
 * All other changes (hasInterviews, My Interviews, etc.) are unchanged.
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard, Users, Briefcase, FileText, CreditCard,
    LogOut, Menu, X, Calendar, Settings, ShieldAlert, UserPlus,
    User, ShieldCheck, Shield, BookOpen, Building2, BarChart2,
    Tag, Layers, MapPin, Target, Clock, GitBranch, Calculator,
    UserCheck, UserMinus, HelpCircle, Receipt, TrendingUp, IndianRupee,
    MessageSquare, Zap, Home, Bot, Star, ClipboardList, Bell, LogIn
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axiosForNav from '../../api/axiosConfig';
import { Button, Dropdown } from 'react-bootstrap';
import NotificationBell from '../notifications/NotificationBell';

const IconMap = {
    LayoutDashboard, Users, Briefcase, FileText, CreditCard,
    LogOut, Menu, X, Calendar, Settings, ShieldAlert, UserPlus,
    User, ShieldCheck, Shield, BookOpen, Building2, BarChart2,
    Tag, Layers, MapPin, Target, Clock, GitBranch, Calculator,
    UserCheck, UserMinus, HelpCircle, Receipt, TrendingUp, IndianRupee,
    MessageSquare, Zap, Home, Bot, Star, ClipboardList, Bell, LogIn
};

const CompanyLogo = ({ logoUrl, companyName = '', size = 32 }) => {
    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/v1', '');
    const initials = (companyName || 'PD')
        .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
    if (logoUrl) {
        return (
            <img
                src={logoUrl.startsWith('http') ? logoUrl : `${apiBase}${logoUrl}`}
                alt={companyName}
                style={{
                    width: size, height: size, objectFit: 'contain', borderRadius: 6,
                    background: 'rgba(255,255,255,0.12)', padding: 2, flexShrink: 0,
                }}
            />
        );
    }
    return (
        <div style={{
            width: size, height: size, borderRadius: 6, flexShrink: 0,
            background: 'var(--primary-color,#4f46e5)', color: '#fff',
            fontWeight: 700, fontSize: Math.round(size * 0.38),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {initials}
        </div>
    );
};

// ── ALL_NAV ──────────────────────────────────────────────────────────────────
const ALL_NAV = [
    // ── 1. Common ─────────────────────────────────────────────────────────
    {
        name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard',
        roles: ['super-admin','admin','hr-manager','hiring-manager','recruiter',
                'employee','account-manager','reporting-manager'],
    },
    { name: 'ESS Dashboard',     path: '/my-profile',            icon: 'LayoutDashboard', roles: ['employee','admin','hr-manager','hiring-manager','recruiter','account-manager','reporting-manager'] },
    // ── 2. Super Admin ────────────────────────────────────────────────────
    { name: 'Super Center',      path: '/superadmin/dashboard',  icon: 'ShieldAlert',  roles: ['super-admin'] },
    { name: 'Companies',         path: '/superadmin/companies',  icon: 'Building2',    roles: ['super-admin'] },
    { name: 'Pricing Plans',     path: '/superadmin/plans',      icon: 'Tag',          roles: ['super-admin'] },
    { name: 'Promo Codes',       path: '/superadmin/promo-codes',icon: 'Tag',          roles: ['super-admin'] },
    { name: 'Inquiries & Demos', path: '/superadmin/inquiries',  icon: 'MessageSquare',roles: ['super-admin'] },
    { name: 'System Logs',       path: '/superadmin/logs',       icon: 'Shield',       roles: ['super-admin'] },
    { name: 'Platform Settings', path: '/superadmin/settings',   icon: 'Settings',     roles: ['super-admin'] },
    { name: 'Login Logs',        path: '/superadmin/login-logs', icon: 'LogIn',        roles: ['super-admin'] },
    { name: 'Abandoned Signups', path: '/superadmin/signup-intents', icon: 'UserPlus', roles: ['super-admin'] },
    // ── 3. People & Org ───────────────────────────────────────────────────
    { name: 'Employees',   path: '/employees',   icon: 'Users',    roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Departments', path: '/departments', icon: 'Layers',   roles: ['admin','hr-manager','account-manager'] },
    { name: 'Locations',   path: '/locations',   icon: 'MapPin',   roles: ['admin'] },
    { name: 'Shifts',      path: '/shifts',      icon: 'Clock',    roles: ['admin','hr-manager','account-manager'] },
    { name: 'Shift Roster', path: '/shifts/roster', icon: 'Calendar', roles: ['admin','hr-manager','account-manager'] },
    {
        name: 'Org Chart', path: '/org-chart', icon: 'GitBranch',
        roles: ['super-admin','admin','hr-manager','account-manager','reporting-manager','employee'],
    },
    {
        name: 'Announcements', path: '/announcements', icon: 'Bell',
        roles: ['super-admin','admin','hr-manager','hiring-manager','recruiter','employee','account-manager','reporting-manager'],
    },
    { name: 'Loans & Advances', path: '/loans', icon: 'IndianRupee', roles: ['admin','hr-manager','account-manager'] },
    { name: 'Probation Tracking', path: '/probation', icon: 'UserCheck', roles: ['admin','hr-manager','account-manager'] },
    // ── 4. Recruitment ────────────────────────────────────────────────────
    {
        name: 'ATS / Jobs', path: '/ats', icon: 'Briefcase',
        roles: ['super-admin','admin','hr-manager','hiring-manager','recruiter','account-manager'],
    },
    {
        name: 'My Interviews', path: '/my-interviews', icon: 'Calendar',
        roles: ['super-admin','admin','hr-manager','hiring-manager','recruiter',
                'employee','account-manager','reporting-manager'],
    },
    // ── 5. Onboarding & Offboarding ───────────────────────────────────────
    { name: 'Onboarding',     path: '/onboarding',                  icon: 'UserCheck', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Offboarding',    path: '/offboarding',                 icon: 'UserMinus', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Exit Interviews',path: '/offboarding/exit-interviews', icon: 'LogOut',    roles: ['admin','hr-manager'] },
    // ── 6. Payroll ────────────────────────────────────────────────────────
    { name: 'Payroll Management', path: '/payroll/management',  icon: 'CreditCard', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Salary Arrears',     path: '/payroll/arrears',     icon: 'Calculator', roles: ['admin','hr-manager','account-manager'] },
    { name: 'Tax Reports',        path: '/payroll/tax-summary', icon: 'BarChart2',  roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Billing & Plans',    path: '/billing',             icon: 'CreditCard', roles: ['admin'] },
    // ── 7. Time & Attendance ──────────────────────────────────────────────
    { name: 'Attendance',      path: '/attendance',            icon: 'Calendar', roles: ['admin','hr-manager','account-manager'] },
    { name: 'Regularization',  path: '/attendance/regularize',icon: 'Clock',    roles: ['admin','hr-manager','account-manager','reporting-manager'] },
    { name: 'Leave',           path: '/leave',                 icon: 'BookOpen', roles: ['admin','hr-manager','account-manager'] },
    { name: 'Timesheet',       path: '/timesheet',             icon: 'Clock',    roles: ['admin','hr-manager','account-manager'] },
    { name: 'Holiday Calendar',path: '/holidays',              icon: 'Calendar', roles: ['admin','hr-manager','account-manager'] },
    { name: 'Remote Work',     path: '/remote-work',           icon: 'Home',     roles: ['super-admin','admin','hr-manager','account-manager'] },
    // ── 8. Expenses ───────────────────────────────────────────────────────
    { name: 'Expenses', path: '/expenses', icon: 'Receipt', roles: ['super-admin','admin','hr-manager','account-manager'] },
    // ── 9. Performance & Development ──────────────────────────────────────
    { name: 'Performance',       path: '/performance', icon: 'TrendingUp', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Training & Learning',path: '/training',   icon: 'BookOpen',   roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Skills Matrix',     path: '/skills',      icon: 'Star',       roles: ['super-admin','admin','hr-manager','account-manager'] },
    // ── 10. Engagement & Compliance ───────────────────────────────────────
    { name: 'Surveys & Engagement', path: '/surveys',             icon: 'MessageSquare', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Compliance Calendar',  path: '/compliance-calendar', icon: 'ClipboardList', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Documents',            path: '/documents',           icon: 'FileText',      roles: ['admin','hr-manager','account-manager'] },
    { name: 'HR Help Desk',         path: '/helpdesk',            icon: 'HelpCircle',    roles: ['super-admin','admin','hr-manager','account-manager'] },
    // ── 11. Analytics & Tools ─────────────────────────────────────────────
    { name: 'Reports & Analytics',  path: '/reports',      icon: 'BarChart2', roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Audit Logs',           path: '/audit-logs',   icon: 'Shield',    roles: ['super-admin','admin','hr-manager','account-manager'] },
    { name: 'Workflow Automation',  path: '/workflows',    icon: 'Zap',       roles: ['super-admin','admin','hr-manager'] },
    { name: 'AI Assistant',         path: '/ai-assistant', icon: 'Bot',       roles: ['super-admin','admin','hr-manager','account-manager','reporting-manager'] },
    // ── 12. Settings ──────────────────────────────────────────────────────
    { name: 'Settings', path: '/settings/branding', icon: 'Settings', roles: ['admin'] },
    // ── 13. Manager (reporting-manager) ───────────────────────────────────
    { name: 'My Team',          path: '/my-team',    icon: 'Users',    roles: ['reporting-manager'] },
    { name: 'Team Leave',       path: '/leave',      icon: 'BookOpen', roles: ['reporting-manager'] },
    { name: 'Team Timesheet',   path: '/timesheet',  icon: 'Clock',    roles: ['reporting-manager'] },
    { name: 'Team Remote Work', path: '/remote-work',icon: 'Home',     roles: ['reporting-manager'] },
    // ── 14. Employee Self-Service ─────────────────────────────────────────
    { name: 'My Profile',    path: '/profile',                        icon: 'User',       roles: ['employee','reporting-manager','admin','hr-manager','account-manager','super-admin'] },
    { name: 'My Payslips',   path: '/payroll/my-payslips',            icon: 'CreditCard', roles: ['employee','admin','hr-manager','account-manager','reporting-manager'] },
    { name: 'My Investment', path: '/payroll/investment-declaration',  icon: 'CreditCard', roles: ['employee','admin','hr-manager','account-manager','reporting-manager'] },
    { name: 'Loans & Advances', path: '/loans', icon: 'IndianRupee', roles: ['employee'] },
    { name: 'My Attendance',          path: '/attendance',            icon: 'Calendar', roles: ['employee'] },
    { name: 'Attendance Regularize',  path: '/attendance/regularize', icon: 'Clock',    roles: ['employee'] },
    { name: 'My Leave',               path: '/leave',                 icon: 'BookOpen', roles: ['employee'] },
    { name: 'My Timesheet',           path: '/timesheet',             icon: 'Clock',    roles: ['employee'] },
    { name: 'WFH / Remote Work',      path: '/remote-work',           icon: 'Home',     roles: ['employee'] },
    { name: 'My Expenses',            path: '/expenses',              icon: 'Receipt',  roles: ['employee'] },
    { name: 'My Performance',         path: '/performance',           icon: 'Target',   roles: ['employee'] },
    { name: 'My Training',            path: '/training',              icon: 'BookOpen', roles: ['employee'] },
    { name: 'My Skills',              path: '/skills',                icon: 'Star',     roles: ['employee'] },
    { name: 'My Documents',           path: '/documents',             icon: 'FileText', roles: ['employee'] },
    { name: 'Holiday Calendar',       path: '/holidays',              icon: 'Calendar', roles: ['employee'] },
    { name: 'Help Desk',              path: '/helpdesk',              icon: 'HelpCircle',roles: ['employee'] },
    { name: 'AI Assistant',           path: '/ai-assistant',          icon: 'Bot',      roles: ['employee'] },
    // { name: 'AI Assistant',           path: '/ai-assistant',          icon: 'Bot',      roles: ['employee'], plans: ['growth','enterprise'] },   // hidden from 'starter'
    { name: 'Exit Interview',         path: '/exit-interview',        icon: 'LogOut',   roles: ['employee'] },
];

// ─────────────────────────────────────────────────────────────────────────────
const DashboardLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
    const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);
    const { user, logout } = useAuth();
    const navigate  = useNavigate();
    const location  = useLocation();

    useEffect(() => {
        if (isMobile) setSidebarOpen(false);
    }, [location.pathname, isMobile]);

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            setSidebarOpen(!mobile);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const c = user?.primary_color || '#4f46e5';
        document.documentElement.style.setProperty('--primary-color', c);
        document.documentElement.style.setProperty('--primary-color-soft', `${c}20`);
    }, [user?.primary_color]);

    const handleLogout = useCallback(
        () => { logout(); navigate('/login'); },
        [logout, navigate]
    );

    // ── Direct reports check ───────────────────────────────────────────────
    const [hasDirectReports, setHasDirectReports] = React.useState(false);
    const reportsFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (reportsFetchedRef.current) return;
        reportsFetchedRef.current = true;
        if (user?.role === 'employee') {
            axiosForNav.get('/employees/my-reports')
                .then(res => setHasDirectReports((res.data.data || []).length > 0))
                .catch(() => {});
        }
    }, [user?.role]);

    // ── Interview check (ALL roles) ────────────────────────────────────────
    const [hasInterviews, setHasInterviews] = React.useState(false);
    const interviewsFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (interviewsFetchedRef.current || !user?.id) return;
        interviewsFetchedRef.current = true;
        axiosForNav.get('/ats/my-interviews')
            .then(res => {
                const list = res.data.data || [];
                setHasInterviews(list.some(iv => iv.status === 'Scheduled'));
            })
            .catch(() => {});
    }, [user?.id]);

    // ── Build nav for this user ────────────────────────────────────────────
    const navItems = useMemo(() => {
        const role = user?.role;
        if (!role) return [];
        const effectiveRoles = [role];
        if (hasDirectReports) effectiveRoles.push('reporting-manager');
        const currentPlanCode = user?.plan_code;
        const includedFeatures = user?.included_features;
        const seenPaths = new Set();
        return ALL_NAV.filter(item => {
            if (!item.roles.some(r => effectiveRoles.includes(r))) return false;
            if (item.path === '/my-interviews' && !hasInterviews) return false;
            if (item.plans && !item.plans.includes(currentPlanCode)) return false;
            if (Array.isArray(includedFeatures) && !includedFeatures.includes(item.path)) return false;
            if (seenPaths.has(item.path)) return false;
            seenPaths.add(item.path);
            return true;
        });
    }, [user?.role, user?.plan_code, user?.included_features, hasDirectReports, hasInterviews]);

    const isSuperAdmin = user?.role === 'super-admin';
    const pageTitle = location.pathname
        .split('/').filter(Boolean).pop()
        ?.replace(/-/g, ' ') || 'Dashboard';
    const sidebarStyle = (!isMobile && !sidebarOpen) ? { display: 'none' } : {};

    return (
        <div className="main-wrapper">
            {sidebarOpen && isMobile && (
                <div
                    onClick={() => setSidebarOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 997, cursor: 'pointer' }}
                />
            )}
            <aside className={`sidebar${sidebarOpen ? ' active' : ''}`} style={sidebarStyle}>
                <div className="sidebar-header d-flex align-items-center justify-content-between mb-4">
                    {isSuperAdmin ? (
                        <div className="d-flex align-items-center gap-2">
                            <img src="/logo.png" alt="PeoplesDock"
                                style={{ height: 70, width: 'auto', objectFit: 'contain' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                        </div>
                    ) : (
                        <div className="d-flex align-items-center gap-2 overflow-hidden">
                            <CompanyLogo logoUrl={user?.logo_url} companyName={user?.company_name} size={32} />
                            <div style={{ overflow: 'hidden' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#272727', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user?.company_name || 'PeoplesDock'}
                                </div>
                                <div style={{ fontSize: 9, color: 'rgba(83,83,83,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 2 }}>
                                    {user?.role?.replace(/-/g, ' ')}
                                </div>
                            </div>
                        </div>
                    )}
                    <Button variant="link" className="d-md-none text-white p-0" onClick={() => setSidebarOpen(false)}>
                        <X size={22} />
                    </Button>
                </div>
                <nav className="sidebar-nav flex-grow-1">
                    {navItems.map(item => {
                        const Icon = IconMap[item.icon];
                        const isActive =
                            location.pathname === item.path ||
                            (item.path !== '/dashboard' &&
                             item.path !== '/leave' &&
                             item.path !== '/attendance' &&
                             item.path !== '/timesheet' &&
                             item.path !== '/remote-work' &&
                             item.path !== '/documents' &&
                             item.path !== '/performance' &&
                             item.path !== '/training' &&
                             item.path !== '/skills' &&
                             item.path !== '/helpdesk' &&
                             item.path !== '/holidays' &&
                             item.path !== '/expenses' &&
                             location.pathname.startsWith(item.path));
                        return (
                            <Link
                                key={`${item.path}-${item.name}`}
                                to={item.path}
                                className={`nav-link-custom${isActive ? ' active' : ''}`}
                            >
                                {Icon && <Icon size={18} />}
                                <span>{item.name}</span>
                            </Link>
                        );
                    })}
                </nav>
                <div className="sidebar-footer border-top pt-3">
                    <div className="p-3 mb-2 rounded-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                                {user?.first_name?.charAt(0) || '?'}
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user?.first_name} {user?.last_name}
                                </div>
                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08rem', marginTop: 2 }}>
                                    {user?.role?.replace(/-/g, ' ')}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="nav-link-custom border-0 bg-transparent w-100" style={{ color: '#f87171' }}>
                        <LogOut size={18} /><span>Logout</span>
                    </button>
                </div>
            </aside>
            <main className={`content-area${isMobile || !sidebarOpen ? ' expanded' : ''}`}>
                <header className="topbar d-flex align-items-center justify-content-between mb-3 p-3 bg-white rounded shadow-sm mx-2 mx-md-3 mt-2">
                    <div className="d-flex align-items-center gap-3">
                        <Button variant="link" className="text-dark p-0" onClick={() => setSidebarOpen(v => !v)}>
                            <Menu size={22} />
                        </Button>
                        <h6 className="mb-0 fw-bold d-none d-md-block text-capitalize">{pageTitle}</h6>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <NotificationBell />
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="link" className="d-flex align-items-center gap-2 text-decoration-none text-dark p-0 border-0">
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700 }}>
                                    {user?.first_name?.charAt(0) || <User size={16} />}
                                </div>
                                <div className="d-none d-sm-block text-start">
                                    <p className="mb-0 fw-bold small">{user?.first_name} {user?.last_name}</p>
                                </div>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="shadow border-0 mt-2">
                                <div className="px-3 py-2 border-bottom mb-1">
                                    <p className="mb-0 fw-bold small">{user?.first_name} {user?.last_name}</p>
                                    <p className="mb-0 text-muted" style={{ fontSize: 11 }}>{user?.email}</p>
                                    <p className="mb-0 text-muted text-capitalize" style={{ fontSize: 10 }}>{user?.role?.replace(/-/g, ' ')}</p>
                                </div>
                                <Dropdown.Item as={Link} to="/profile">My Profile</Dropdown.Item>
                                {user?.role === 'admin' && (
                                    <Dropdown.Item as={Link} to="/settings/branding">Organisation Settings</Dropdown.Item>
                                )}
                                {user?.role === 'super-admin' && (
                                    <Dropdown.Item as={Link} to="/superadmin/settings">Platform Settings</Dropdown.Item>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleLogout} className="text-danger fw-bold">Logout</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </header>
                <div className="page-body px-2 px-md-3 pb-4">
                    <div className="animate-fade-in">{children}</div>
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;
